import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  fetchAccountContacts,
  updateOrderContact,
  OrderConflictError,
  type AccountContact,
} from '../../lib/orders';

interface EditOrderContactDialogProps {
  orderId: string;
  accountId: string;
  currentContactId: string | null;
  expectedLastModifiedDate: string | null;
  onClose: () => void;
  /** refetch is de bron van waarheid voor de contact-weergave; lmd is voor conflict-detectie. */
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

export function EditOrderContactDialog({
  orderId,
  accountId,
  currentContactId,
  expectedLastModifiedDate,
  onClose,
  onSaved,
}: EditOrderContactDialogProps) {
  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>(currentContactId ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchAccountContacts(accountId)
      .then((rows) => {
        if (cancelled) return;
        setContacts(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const changed = (selected || null) !== (currentContactId || null);

  const handleSubmit = async () => {
    if (!changed) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await updateOrderContact(orderId, selected || null, expectedLastModifiedDate ?? undefined);
      onSaved(res.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Contactpersoon wijzigen mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Contactpersoon wijzigen</h2>
          <p className="text-xs text-slate-500 mt-1">
            Kies de contactpersoon van de klant voor deze werk order.
          </p>
        </div>

        <div className="px-6 py-4 space-y-2 max-h-80 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-3 h-3 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
              Contactpersonen laden…
            </div>
          )}

          {loadError && <p className="text-xs text-red-600">{loadError}</p>}

          {!loading && !loadError && (
            <>
              {contacts.map((c) => (
                <label
                  key={c.id}
                  className={clsx(
                    'flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer',
                    selected === c.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  )}
                >
                  <input
                    type="radio"
                    name="contactpersoon"
                    className="mt-1"
                    checked={selected === c.id}
                    onChange={() => setSelected(c.id)}
                    disabled={submitting}
                  />
                  <span className="min-w-0">
                    <span className="text-sm font-medium text-slate-900 block truncate">
                      {c.name || '(geen naam)'}
                      {c.title && (
                        <span className="text-xs font-normal text-slate-500"> — {c.title}</span>
                      )}
                    </span>
                    {c.email && (
                      <span className="text-xs text-slate-500 block truncate">{c.email}</span>
                    )}
                  </span>
                </label>
              ))}

              <label
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer',
                  selected === ''
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:bg-slate-50'
                )}
              >
                <input
                  type="radio"
                  name="contactpersoon"
                  checked={selected === ''}
                  onChange={() => setSelected('')}
                  disabled={submitting}
                />
                <span className="text-sm text-slate-600">Geen contactpersoon</span>
              </label>
            </>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading || !!loadError || !changed}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || loading || !!loadError || !changed
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
